import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import backgroundImage from '../images/10246984.jpeg';
import AUIMG from '../images/AboutUsIMG.jpg';
import './About.css';

// Prevent save and context menu
const usePreventSave = () => {
  useEffect(() => {
    const preventSave = (e) => {
      if (e.ctrlKey && (e.key === 's' || e.key === 'S')) e.preventDefault();
    };
    const preventRightClick = (e) => e.preventDefault();

    document.addEventListener('keydown', preventSave);
    document.addEventListener('contextmenu', preventRightClick);

    return () => {
      document.removeEventListener('keydown', preventSave);
      document.removeEventListener('contextmenu', preventRightClick);
    };
  }, []);
};

// Better desktop detection (media-query based)
const useMediaQuery = (query) => {
  const getMatch = () => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);

    // set immediately + listen for changes
    setMatches(mql.matches);

    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
};

const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');

// Styled components
const AboutContainer = styled.div`
  padding: clamp(3rem, 6vw, 6rem) 0;
  z-index: 2;
  margin-top: 0; /* was -2.5rem */
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: rgb(15, 0, 0);
  min-height: 80vh;
`;

const Container = styled.div`
  max-width: 1200px;
  font-family: "Raleway-Regular";
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 2rem);

  @media (min-width: 2560px) {
    max-width: 1600px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-top: 3rem;
  }

  @media (min-width: 1920px) {
    margin-top: 1rem;
  }

  @media (min-width: 2560px) {
    margin-top: 2rem;
  }
`;

const Title = styled.h1`
  font-family: "Raleway-Regular";
  color: #0a192f;
  font-size: clamp(2rem, 4vw, 2.5rem);
  margin-right: 0.5rem;
`;

const TitleLogo = styled.h1`
  font-family: "Righteous-Regular";
  color: #0a192f;
  font-size: clamp(2rem, 4vw, 2.5rem);
`;

const Subtitle = styled.p`
  color: #0a192f;
  font-family: "Helvetica";
  font-size: clamp(1rem, 2vw, 1.25rem);
  margin-bottom: 5rem;
  margin-top: 1rem;
  text-align: center;
`;

const Grid = styled.div`
  font-family: "Helvetica";
  display: grid;
  color: #0a192f;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 6rem;
  margin-top: 6rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Image = styled.img`
  width: 100%;
  height: clamp(300px, 50vw, 400px);
  object-fit: cover;
  border-radius: 0.5rem;
  user-select: none;
  pointer-events: none;
`;

/**
 * Keep your 3 cards in the SAME 3-column layout,
 * but each card is its own container (1, 2, 3) to avoid any odd cropping/overflow.
 */
const AccordionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  align-items: start; /* IMPORTANT: prevents equal-height stretch */

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
// Base “card” (shared styles for 1/2/3)
const AccordionCardBase = styled.div`
  background-color: hsla(216, 65%, 11%, 0.45);
  border-radius: 0.5rem;
  overflow: hidden;
  height: fit-content;
  align-self: start; /* extra safety */
`;

// Separate containers (requested)
const AccordionContainer1 = styled(AccordionCardBase)``;
const AccordionContainer2 = styled(AccordionCardBase)``;
const AccordionContainer3 = styled(AccordionCardBase)``;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  background-color: rgba(10, 25, 47, 0.45);
  color: #cec9ab;
  user-select: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: hsla(216, 65%, 11%, 0.6);
  }

  h3 {
    margin: 0;
    pointer-events: none;
  }
`;

const AccordionContentInner = styled.div`
  padding: 1rem;
  background-color: hsla(216, 64.9%, 11.2%, 0.39);
  color: #cec9ab;
  font-family: 'Helvetica';
  line-height: 1.5;

  p {
    margin: 0;
  }
`;

// Accordion content with animation
const AccordionContent = ({ isOpen, children }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen && ref.current) setHeight(ref.current.scrollHeight);
    else setHeight(0);
  }, [isOpen, children]);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          style={{ overflow: 'hidden' }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <AccordionContentInner ref={ref}>{children}</AccordionContentInner>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Single card accordion (reusable)
const SingleAccordionCard = ({ title, content, isOpen, onToggle }) => {
  return (
    <>
      <AccordionHeader
        onClick={onToggle}
        style={{
          backgroundColor: isOpen ? 'rgba(10, 25, 47, 0.6)' : 'rgba(10, 25, 47, 0.45)',
        }}
      >
        <h3>{title}</h3>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </AccordionHeader>

      <AccordionContent isOpen={isOpen}>
        <p>{content}</p>
      </AccordionContent>
    </>
  );
};

// Updated AccordionSection:
// - Desktop (>=1024px): click any header toggles ALL open/close
// - Mobile: each card toggles independently (so it “works as expected”)
const AccordionSection = ({ items }) => {
  const isDesktop = useIsDesktop();

  // Desktop: one boolean controls all 3
  const [desktopAllOpen, setDesktopAllOpen] = useState(false);

  // Mobile: 3 independent open states
  const [mobileOpen, setMobileOpen] = useState(() => items.map(() => false));

  // When breakpoint changes, reset to avoid weird mixed states
  useEffect(() => {
    setDesktopAllOpen(false);
    setMobileOpen(items.map(() => false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const toggleIndex = (index) => {
    if (isDesktop) {
      setDesktopAllOpen((v) => !v);
    } else {
      setMobileOpen((prev) => prev.map((v, i) => (i === index ? !v : v)));
    }
  };

  const isOpenAt = (index) => (isDesktop ? desktopAllOpen : !!mobileOpen[index]);

  // Each card in its own container (1/2/3)
  const CardWrap = [AccordionContainer1, AccordionContainer2, AccordionContainer3];

  return (
    <AccordionRow>
      {items.map((item, index) => {
        const Wrap = CardWrap[index] || AccordionContainer1; // fallback if you add more later
        const isOpen = isOpenAt(index);

        return (
          <Wrap key={`accordion-${index}`}>
            <SingleAccordionCard
              title={item.title}
              content={item.content}
              isOpen={isOpen}
              onToggle={() => toggleIndex(index)}
            />
          </Wrap>
        );
      })}
    </AccordionRow>
  );
};

const About = () => {
  usePreventSave();

  const accordionData = [
    {
      title: 'Our Vision',
      content:
        'We envision a future where VR seamlessly integrates within Real Estate and Interior Design, revolutionizing how people experience spaces before construction or renovation.',
    },
    {
      title: 'Our Approach',
      content:
        'Discuss, implement and finalise your expectations presenting you with a visual journey of your project. NueDesign combines cutting-edge VR technology with intuitive design tools, focusing on user-friendly interfaces, photorealistic renderings, and real-time collaboration.',
    },
    {
      title: 'Our Technology',
      content:
        'We use state-of-the-art VR hardware such as Meta Quest 3, coupled with proprietary software to visualize spaces prior to commitment.',
    },
  ];

  return (
    <AboutContainer>
      <Container>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <TitleContainer>
            <Title>About</Title>
            <TitleLogo>NueDesign</TitleLogo>
          </TitleContainer>
          <Subtitle>Revolutionizing property experiences through virtual reality</Subtitle>
        </motion.div>

        <Grid>
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <Image src={AUIMG} alt="AboutUS" onContextMenu={(e) => e.preventDefault()} />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2>Our Mission</h2>
            <p>
              At NueDesign, we're transforming how anyone can visualise, customise and experience properties before
              committing to guarantee the desired outcome. By harnessing virtual reality, we're making property design
              and visualization more accessible, efficient, and immersive.
            </p>
            <p>
              Founded in 2023 and released in 2025, we're delighted to bring VR at your doorstep, assisting with your
              real estate and design needs, empowering professionals to take it a step further. Satisfied clients is our
              Ultimate Goal, directly or through you.
            </p>
          </motion.div>
        </Grid>

        <AccordionSection items={accordionData} />
      </Container>
    </AboutContainer>
  );
};

export default About;
